import React from "react";
import { useController } from "react-hook-form";
import { validateNoBlank } from "../utils/validateUtils";

import { PrefixFormItemProps } from "./PrefixFormItem.types";

function PrefixFormItem(props: PrefixFormItemProps): JSX.Element {
  const {
    children,
    defaultValue,
    name,
    rules,
    getChildrenProps,
    prefixRules,
    prefixName,
    prefixDefaultValue,
  } = props;

  const child = React.Children.only(children) as React.ReactElement;
  const required = Boolean(rules?.required);
  const fieldName = child.props["label"] || child.props["placeholder"];
  const prefixRequired = Boolean(prefixRules?.required);
  const prefixFieldName = child.props["prefixLabel"];

  let customValidate = rules?.validate;
  if (required && fieldName) {
    const nonBlankValidation = (v: any) => validateNoBlank(v, fieldName);
    customValidate =
      customValidate instanceof Function
        ? { customValidate, nonBlankValidation }
        : { ...customValidate, nonBlankValidation };
  }

  let prefixCustomValidate = prefixRules?.validate;
  if (prefixRequired && prefixFieldName) {
    const nonBlankValidation = (v: any) => validateNoBlank(v, prefixFieldName);
    prefixCustomValidate =
      prefixCustomValidate instanceof Function
        ? { prefixCustomValidate, nonBlankValidation }
        : { ...prefixCustomValidate, nonBlankValidation };
  }

  const { field: prefixField, fieldState: prefixFieldState } = useController({
    name: prefixName,
    rules: { ...prefixRules, validate: prefixCustomValidate },
    defaultValue: prefixDefaultValue,
  });

  const { field, fieldState, formState } = useController({
    name,
    rules: { ...rules, validate: customValidate },
    defaultValue,
  });

  const { onChange, onBlur, value, ref } = field;
  const {
    onChange: onPrefixChange,
    onBlur: onPrefixBlur,
    value: prefix,
    ref: prefixRef,
  } = prefixField;
  const { error } = fieldState;
  const { error: prefixError } = prefixFieldState;

  const extraProps = React.useMemo(() => {
    return getChildrenProps?.({ field, fieldState, formState });
  }, [field, fieldState, formState, getChildrenProps]);

  const helperText =
    prefixError?.message || error?.message || prefixError?.type || error?.type;

  const childProps = {
    error: Boolean(error || prefixError),
    helperText,
    onBlur,
    onChangeValue: onChange,
    onPrefixBlur,
    onPrefixChange,
    prefix,
    prefixRequired,
    required,
    value,
    inputRef: ref,
    prefixInputRef: prefixRef,
    ...extraProps,
  };

  return React.cloneElement(child, childProps);
}

export default PrefixFormItem;
